import React, { useCallback, useMemo } from "react";
import { FiltersTypes } from "@common/types/filters/general";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { FinancialDocumentView } from "@common/types/views/financialDocumentViews";
import { View } from "@common/types/views/views";
import { financialDocumentsViewConfig } from "@components/documents/FinancialDocumentsView/financialDocumentsViewConfig";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import type { PaginationType } from "@hooks/paginationMode";
import { useOverlays } from "contexts/OverlayContext";
import { restrictViewConfigColumns } from "../shared/taskTableView";
import { LockedTaskFilters } from "../Transactions/LockedTaskFilters";
import { getFinancialDocumentsLockedActiveFilters } from "./financialDocumentsTaskFilters";
import { omitTaskScope } from "./financialDocumentsTaskView";
import { FinancialDocumentsTaskAskButton } from "./FinancialDocumentsTaskAskButton";
import { FinancialDocumentsTaskTableProvider } from "./FinancialDocumentsTaskTableProvider";
import { FinancialDocumentsTaskViewContainer } from "./FinancialDocumentsTaskViewContainer";
import { useFinancialDocumentsTaskRows } from "./useFinancialDocumentsTaskRows";

const TASK_SELECTION_ACTIONS = <FinancialDocumentsTaskAskButton />;

interface FinancialDocumentsTaskTableProps {
    view: FinancialDocumentView;
    onViewChange: (view: View) => void;
    onFiltersChange: FiltersChangeFn;
    availableFilters: FilterTabs[];
    defaultView: FinancialDocumentView;
    allowedColumnIds?: ReadonlySet<string>;
    originalFilters?: FiltersTypes;
    stickyDocumentIds?: number[];
    emptyView: () => React.ReactNode;
    className?: string;
    isFullPage?: boolean;
    /** Numbered pages by default; the task body loads more on scroll. */
    paginationType?: PaginationType;
}

export const FinancialDocumentsTaskTable: React.FC<
    FinancialDocumentsTaskTableProps
> = ({
    view,
    onViewChange,
    onFiltersChange,
    availableFilters,
    defaultView,
    allowedColumnIds,
    originalFilters,
    stickyDocumentIds,
    emptyView,
    className,
    isFullPage = true,
    paginationType,
}) => {
    const { openOverlay } = useOverlays();
    const {
        queryFilters,
        rows,
        total,
        totalPages,
        page,
        setPage,
        resetPage,
        sort,
        handleSortChange,
        loadMore,
    } = useFinancialDocumentsTaskRows({
        viewFilters: view.filters,
        originalFilters,
        stickyDocumentIds,
        paginationType,
    });

    const pagination = useMemo(
        () =>
            loadMore
                ? undefined
                : { currentPage: page, totalPages, onPageChange: setPage },
        [loadMore, page, totalPages, setPage],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (payload) => {
            resetPage();
            onFiltersChange(payload);
        },
        [onFiltersChange, resetPage],
    );
    const handleDocumentClick = useCallback(
        (document?: FinancialDocumentListItem) => {
            if (document) {
                openOverlay("financialDocumentDetailsModal", document.id);
            }
        },
        [openOverlay],
    );

    const columnSettingsConfig = useMemo(
        () =>
            restrictViewConfigColumns({
                config: financialDocumentsViewConfig,
                allowedColumnIds,
            }),
        [allowedColumnIds],
    );
    const overlayFilters = useMemo(
        () => omitTaskScope(view.filters),
        [view.filters],
    );
    const lockedActiveFilters = useMemo(
        () => getFinancialDocumentsLockedActiveFilters(originalFilters),
        [originalFilters],
    );

    return (
        <FinancialDocumentsTaskTableProvider
            view={view}
            queryFilters={queryFilters}
            total={total}
        >
            <TablePageHeader
                search={view.filters.search}
                isFullPage={isFullPage}
                onSearchChange={(value) =>
                    handleFiltersChange({ search: value ?? "" })
                }
                className={className}
                filters={overlayFilters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                shouldShowNoAccountOption
                afterFiltersChildren={
                    originalFilters ? (
                        <LockedTaskFilters
                            lockedActiveFilters={lockedActiveFilters}
                            baseFilters={omitTaskScope(originalFilters)}
                            maxVisibleFilters={lockedActiveFilters.length}
                        />
                    ) : undefined
                }
            >
                <ColumnSettings
                    view={view}
                    viewConfig={columnSettingsConfig}
                    defaultView={defaultView}
                    onViewChange={onViewChange}
                />
            </TablePageHeader>
            <FinancialDocumentsTaskViewContainer
                view={view}
                documents={rows}
                total={total}
                pagination={pagination}
                onDocumentClick={handleDocumentClick}
                sort={sort}
                onSortChange={handleSortChange}
                emptyView={emptyView}
                onViewChange={onViewChange}
                selectionActions={TASK_SELECTION_ACTIONS}
                loadMore={loadMore}
            />
        </FinancialDocumentsTaskTableProvider>
    );
};
